import React, { useEffect, useState } from 'react';

import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { useGetWidgetCardData, useMetricTracker } from '@entities/widget-card/model';
import { IWidgetCard, IWidgetCategoriesList } from '@entities/widget-card/model/types';
import { useDetailsLightBox } from '@features/DetailsWidgetModal/model/useDetailsLightBox';
import { htmlParser } from '@shared/helpers';
import { CustomModal, EmblaCarousel, Icon, Lightbox, UiButton } from '@shared/ui';

import cls from './DetailsWidgetModal.module.css';

interface IDetailsWidgetModalProps {
    action: ({
        isWidgetFree,
        status,
        widgetId,
    }: {
        isWidgetFree: boolean;
        status?: IWidgetCard['status'];
        widgetId?: IWidgetCard['id'];
        availableActionType?: IWidgetCard['availableActionType'];
        notMyCompany?: IWidgetCategoriesList['context']['notMyCompany'];
    }) => void;
    fromWidgetPage: boolean;
    onCloseModal: () => void;
    currentWidgetId: IWidgetCard['id'] | null;
    isOpen: boolean;
    classes?: string;
}

export const DetailsWidgetModal = ({
    action,
    currentWidgetId,
    fromWidgetPage,
    isOpen,
    onCloseModal,
    classes,
}: IDetailsWidgetModalProps) => {
    const { t } = useTranslation();
    const [cachedWidgetCardData, setCachedWidgetCardData] = useState<IWidgetCard | null>(null);
    const { trackers } = useMetricTracker();

    const { widgetCardData, widgetContext, SLIDES } = useGetWidgetCardData({
        pickedWidgetId: currentWidgetId,
        fromWidgetPage,
    });

    useEffect(() => {
        if (!isOpen || !widgetCardData) return;

        setCachedWidgetCardData(widgetCardData);
    }, [isOpen, widgetCardData]);

    const displayedWidgetCardData = isOpen ? widgetCardData : cachedWidgetCardData;
    const {
        displayedSlides,
        files,
        currentFileId,
        lightBoxFiles,
        onShowLightBox,
        onCloseLightBox,
        currentFile,
    } = useDetailsLightBox({ displayedWidgetCardData, slides: SLIDES });

    let actionButtonText = t('WidgetCard.btn.buy');

    if (displayedWidgetCardData?.availableActionType === 'prolong') {
        actionButtonText = t('WidgetCard.btn.prolong');
    } else if (displayedWidgetCardData?.availableActionType === 'upgrade_tariff') {
        actionButtonText = t('WidgetCard.btn.upgrade_tariff');
    }

    return (
        <CustomModal
            classes={classes}
            isOpen={isOpen}
            onHideModal={() => {
                onCloseModal();
                onCloseLightBox();
            }}
            closeButtonClass={cls.closeBtn}
        >
            <CustomModal.Header>{displayedWidgetCardData?.title}</CustomModal.Header>
            <CustomModal.Body width="569px" classes={cls.modalBody}>
                <EmblaCarousel
                    nextIcon={<Icon iconName="carouselNext" width={17} height={16} />}
                    prevIcon={<Icon iconName="carouselPrev" width={17} height={16} />}
                    slides={displayedSlides}
                    onShowLightBox={onShowLightBox}
                    onThumbClick={onShowLightBox}
                    files={lightBoxFiles}
                    EmblaSlideClassName={cls.EmblaSlideClassName}
                    EmblaSlideNumberClassName={cls.EmblaSlideNumberClassName}
                    SlideImgClassName={cls.SlideImgClassName}
                />
                <div className={cls.description}>
                    {htmlParser(displayedWidgetCardData?.fullDescriptionData?.text)}
                </div>
            </CustomModal.Body>
            <CustomModal.Footer classes={cls.detailsModalFooter}>
                {displayedWidgetCardData?.fullDescriptionData?.videoUrl && (
                    <a
                        href={displayedWidgetCardData?.fullDescriptionData?.videoUrl}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                            textDecoration: 'none',
                            marginRight: 'auto',
                        }}
                    >
                        <UiButton
                            btnText={t('WidgetCard.btn.videoInstruction')}
                            btnColor="secondary"
                            className={cls.detailsModalBtn}
                        />
                    </a>
                )}

                {displayedWidgetCardData?.availableActionType === 'none' ||
                (displayedWidgetCardData?.isFree &&
                    displayedWidgetCardData?.availableActionType === 'prolong') ? null : (
                    <UiButton
                        btnText={actionButtonText}
                        className={cn(
                            cls.actionBtn,
                            displayedWidgetCardData?.availableActionType === 'prolong'
                                ? cls.increaseTariffBtn
                                : cls.detailsModalBtn
                        )}
                        onClick={() => {
                            onCloseModal();
                            if (displayedWidgetCardData?.availableActionType === 'prolong') {
                                trackers.prolong({
                                    widget: displayedWidgetCardData,
                                });
                            }
                            if (displayedWidgetCardData?.availableActionType === 'buy') {
                                trackers.connect({
                                    widget: displayedWidgetCardData,
                                });
                            }
                            action?.({
                                isWidgetFree: displayedWidgetCardData?.isFree,
                                status: displayedWidgetCardData?.status,
                                availableActionType: displayedWidgetCardData?.availableActionType,
                                notMyCompany: widgetContext?.notMyCompany,
                                widgetId: displayedWidgetCardData?.id,
                            });
                        }}
                    />
                )}
            </CustomModal.Footer>
            {currentFileId !== null && currentFileId !== undefined && currentFile && (
                <Lightbox
                    currentFileId={Number(currentFileId)}
                    onClose={onCloseLightBox}
                    files={files}
                />
            )}
        </CustomModal>
    );
};
